import { useCallback, useEffect, useState } from "react";

import { useToasts } from "react-toast-notifications";

import { usePrettyLogEventPage } from "@components/v2/analytics/hooks";
import {
  FETCH_PARTNER_CONSENT_PERMISSIONS,
  GRANT_PARTNER_CONSENT,
  REVOKE_PARTNER_CONSENT,
} from "@gql/partner-consent";
import {
  useFeatureFlags,
  useNorthbeamMutation,
  useNorthbeamQuery,
} from "@hooks/http";
import { FETCH_USER_DETAILS } from "@hooks/http/use-user-enrichment";
import { type User } from "@internal/user";
import {
  type Partner,
  type FetchPartnerConsentPermissions,
  type FetchPartnerConsentPermissionsVariables,
  type GrantPartnerConsent,
  type GrantPartnerConsentVariables,
  type RevokePartnerConsent,
  type RevokePartnerConsentVariables,
  type PartnerPermissionEnum,
} from "@north-beam/nb-client-side";
import {
  WHITELISTED_NB_USERS_FOR_PARTNER_CONSENT,
  PARTNERS_SUPPORTING_ENHANCED_CLICKS_AND_VIEWS,
  FEATURE_FLAG_FOR_PARTNER_CONSENT_MAPPING,
  DISPLAY_NAME_FOR_PARTNER_MAPPING,
} from "@north-beam/nb-common/constants";

export const usePartnerConsent = ({
  user,
  partner,
  permissions,
  hideWhenMissingConsent = false,
}: {
  user: User;
  partner: Partner;
  permissions: PartnerPermissionEnum[];
  hideWhenMissingConsent?: boolean;
}) => {
  const prettyLogEventPage = usePrettyLogEventPage();

  const [hasProvidedConsent, setHasProvidedConsent] = useState(false);
  const { hasFeatureFlag } = useFeatureFlags();
  const { addToast } = useToasts();

  const partnerDisplayName = DISPLAY_NAME_FOR_PARTNER_MAPPING[partner];

  const enhancedModelUpdatedNotificationMessages = {
    modelActivated:
      "Your Clicks + Deterministic Views Attribution Model will become available in the next 2 business days",
    partnerDataIncluded: `Your ${partnerDisplayName} data will be included in the Clicks + Deterministic Views Attribution Model`,
    partnerDataRemoved: `Your ${partnerDisplayName} data will be removed from the Clicks + Deterministic Views Attribution Model`,
    modelDeactivated:
      "Your Clicks + Deterministic Views Attribution Model will be removed",
  };

  const { data, loading } = useNorthbeamQuery<
    FetchPartnerConsentPermissions,
    FetchPartnerConsentPermissionsVariables
  >(FETCH_PARTNER_CONSENT_PERMISSIONS, { variables: { partner } });
  const partnerConsent = data?.me?.partnerConsentPermissions;

  const notifyUserOnUpdatedEnhancedModel = useCallback(
    (message: string) => {
      if (PARTNERS_SUPPORTING_ENHANCED_CLICKS_AND_VIEWS.includes(partner)) {
        addToast(message, { appearance: "success", autoDismiss: false });
      }
    },
    [addToast, partner],
  );

  const [grantPartnerConsent, { loading: isGrantingConsent }] =
    useNorthbeamMutation<GrantPartnerConsent, GrantPartnerConsentVariables>(
      GRANT_PARTNER_CONSENT,
      {
        onCompleted: ({ grantPartnerConsent: response }) => {
          const { enhancedModelActivated } = response;

          const notificationMessage = enhancedModelActivated
            ? enhancedModelUpdatedNotificationMessages.modelActivated
            : enhancedModelUpdatedNotificationMessages.partnerDataIncluded;

          setHasProvidedConsent(true);
          notifyUserOnUpdatedEnhancedModel(notificationMessage);
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          { query: FETCH_PARTNER_CONSENT_PERMISSIONS, variables: { partner } },
          { query: FETCH_USER_DETAILS },
        ],
      },
    );

  const [revokePartnerConsent, { loading: isRevokingConsent }] =
    useNorthbeamMutation<RevokePartnerConsent, RevokePartnerConsentVariables>(
      REVOKE_PARTNER_CONSENT,
      {
        onCompleted: ({ revokePartnerConsent: response }) => {
          const { enhancedModelDeactivated } = response;

          const notificationMessage = enhancedModelDeactivated
            ? enhancedModelUpdatedNotificationMessages.modelDeactivated
            : enhancedModelUpdatedNotificationMessages.partnerDataRemoved;

          setHasProvidedConsent(false);
          notifyUserOnUpdatedEnhancedModel(notificationMessage);
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          { query: FETCH_PARTNER_CONSENT_PERMISSIONS, variables: { partner } },
          { query: FETCH_USER_DETAILS },
        ],
      },
    );

  useEffect(() => {
    const permissionsProvidedForPartner = partnerConsent?.permissions ?? [];
    const partnerHasRequiredPermissions = permissions.every(
      (requiredPermission) =>
        permissionsProvidedForPartner.includes(requiredPermission),
    );
    setHasProvidedConsent(partnerHasRequiredPermissions);
  }, [partnerConsent, permissions]);

  const togglePartnerConsent = useCallback(() => {
    const toggleAction = hasProvidedConsent ? "revoked" : "granted";
    prettyLogEventPage({
      domain: "PartnerConsent",
      action: "onClick",
      additionalActionInfo: `${toggleAction}-${partner}`,
      data: {
        partner,
        action: toggleAction,
        permissions,
      },
    });

    hasProvidedConsent
      ? revokePartnerConsent({ variables: { partner, permissions } })
      : grantPartnerConsent({ variables: { partner, permissions } });
  }, [
    prettyLogEventPage,
    grantPartnerConsent,
    hasProvidedConsent,
    partner,
    permissions,
    revokePartnerConsent,
  ]);

  const canToggleConsent =
    user.isTrueAccountOwner ||
    (PARTNERS_SUPPORTING_ENHANCED_CLICKS_AND_VIEWS.includes(partner) &&
      WHITELISTED_NB_USERS_FOR_PARTNER_CONSENT.includes(user.authedUser.email));

  const shouldHideConsent = hideWhenMissingConsent && !hasProvidedConsent;

  const featureFlagName = FEATURE_FLAG_FOR_PARTNER_CONSENT_MAPPING[partner];

  const canViewConsent =
    featureFlagName && hasFeatureFlag(featureFlagName) && !shouldHideConsent;
  const isUpdatingConsent = isGrantingConsent || isRevokingConsent;

  return {
    loading,
    partnerConsent,
    hasProvidedConsent,
    canViewConsent,
    canToggleConsent,
    grantPartnerConsent,
    isGrantingConsent,
    revokePartnerConsent,
    togglePartnerConsent,
    isRevokingConsent,
    isUpdatingConsent,
  };
};
